import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { Loader2, Plug, Save, ShieldOff } from "lucide-react";
import { api } from "@/lib/api/client";
import { PageHeader } from "@/components/ui/PageHeader";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

/** Dữ liệu GET /api/ai/config — khớp AiController.getConfig() (apps/api). */
type AiConfigDto = {
  baseUrl: string;
  modelSpellcheck: string;
  modelSummary: string;
  modelVision: string;
  enableSpellcheck: boolean;
  enableSummary: boolean;
  monthlyBudgetUsd: number;
  perUserDailyLimit: number;
  /** Task 13/C2: chuỗi mã luật bị tắt, phân tách dấu phẩy (vd "PAGE_SIZE_NOT_A4,..."). */
  disabledRules: string;
  hasKey: boolean;
  keyHint: string | null;
};

type FormState = {
  baseUrl: string;
  modelSpellcheck: string;
  modelSummary: string;
  modelVision: string;
  enableSpellcheck: boolean;
  enableSummary: boolean;
  monthlyBudgetUsd: string;
  perUserDailyLimit: string;
  disabledRules: string;
  apiKeyInput: string;
};

function toForm(c: AiConfigDto): FormState {
  return {
    baseUrl: c.baseUrl,
    modelSpellcheck: c.modelSpellcheck,
    modelSummary: c.modelSummary,
    modelVision: c.modelVision,
    enableSpellcheck: c.enableSpellcheck,
    enableSummary: c.enableSummary,
    monthlyBudgetUsd: String(c.monthlyBudgetUsd),
    perUserDailyLimit: String(c.perUserDailyLimit),
    disabledRules: c.disabledRules,
    apiKeyInput: "",
  };
}

const inputClass =
  "w-full h-9 px-3 rounded-md border border-input bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col sm:grid sm:grid-cols-[220px_1fr] sm:items-start gap-1.5 sm:gap-3 py-2">
      <label className="text-sm text-muted-foreground pt-1.5">{label}</label>
      <div className="min-w-0 max-w-md">
        {children}
        {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
      </div>
    </div>
  );
}

/**
 * Màn quản trị "Cấu hình AI" — CHỈ ADMIN gọi được (GET/PUT /api/ai/config).
 * Người dùng thường bấm vào sẽ nhận 403 — phải hiện "Bạn không có quyền",
 * không được hiện màn vỡ hay quay vòng mãi.
 */
export function AiConfigPage() {
  const qc = useQueryClient();
  const hydrated = useRef(false);
  const [form, setForm] = useState<FormState | null>(null);
  const [saveMsg, setSaveMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [testMsg, setTestMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [testing, setTesting] = useState(false);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["ai-config"],
    queryFn: async () => {
      const res = await api.get("/ai/config");
      return (res.data?.data ?? res.data) as AiConfigDto;
    },
    retry: false,
  });

  useEffect(() => {
    if (data && !hydrated.current) {
      setForm(toForm(data));
      hydrated.current = true;
    }
  }, [data]);

  const forbidden = (error as AxiosError | undefined)?.response?.status === 403;

  async function handleSave() {
    if (!form) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      const body: Record<string, string | number | boolean> = {
        base_url: form.baseUrl.trim(),
        model_spellcheck: form.modelSpellcheck.trim(),
        model_summary: form.modelSummary.trim(),
        model_vision: form.modelVision.trim(),
        enable_spellcheck: form.enableSpellcheck,
        enable_summary: form.enableSummary,
        monthly_budget_usd: Number(form.monthlyBudgetUsd),
        per_user_daily_limit: Number(form.perUserDailyLimit),
        disabled_rules: form.disabledRules.trim(),
      };
      if (form.apiKeyInput.trim()) body.apiKey = form.apiKeyInput.trim();
      await api.put("/ai/config", body);
      setSaveMsg({ ok: true, text: "Đã lưu cấu hình." });
      hydrated.current = false; // để hydrate lại từ dữ liệu mới (keyHint đổi sau khi đổi khoá)
      await qc.invalidateQueries({ queryKey: ["ai-config"] });
    } catch (err) {
      const ax = err as AxiosError<{ msg?: string }>;
      setSaveMsg({ ok: false, text: ax.response?.data?.msg ?? "Lưu cấu hình thất bại." });
    } finally {
      setSaving(false);
    }
  }

  // KHÔNG có endpoint gọi thử trực tiếp tới nhà cung cấp AI trong phạm vi API
  // hiện có (chỉ có GET/PUT config) — CỐ TÌNH không dựng, vì một nút gọi thật
  // sẽ tốn tiền mỗi lần bấm. "Kiểm tra cấu hình" ở đây chỉ xác nhận cấu hình
  // đã lưu trên máy chủ có hợp lệ về định dạng (endpoint đúng URL, đã có khoá
  // hay chưa) — nút và chữ trên giao diện phải khớp đúng việc nó làm, không
  // được gọi là "kết nối" kẻo người dùng hiểu nhầm là đã gọi thử tới nhà cung
  // cấp AI thật.
  async function handleTestConfig() {
    setTesting(true);
    setTestMsg(null);
    try {
      const res = await api.get("/ai/config");
      const c = (res.data?.data ?? res.data) as AiConfigDto;
      let urlOk = true;
      try {
        const u = new URL(c.baseUrl);
        urlOk = u.protocol === "http:" || u.protocol === "https:";
      } catch {
        urlOk = false;
      }
      if (!urlOk) {
        setTestMsg({ ok: false, text: `Endpoint "${c.baseUrl}" không phải URL hợp lệ.` });
      } else if (!c.hasKey) {
        setTestMsg({ ok: false, text: "Chưa cấu hình khoá API — nhập khoá rồi bấm Lưu." });
      } else {
        setTestMsg({
          ok: true,
          text: `Cấu hình hợp lệ: endpoint "${c.baseUrl}", khoá API ${c.keyHint}.`,
        });
      }
    } catch (err) {
      const ax = err as AxiosError<{ msg?: string }>;
      setTestMsg({ ok: false, text: ax.response?.data?.msg ?? "Không kết nối được tới máy chủ." });
    } finally {
      setTesting(false);
    }
  }

  if (isLoading) {
    return (
      <div>
        <PageHeader title="Cấu hình AI" breadcrumb="Quản trị › Cấu hình AI" />
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-10 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" /> Đang tải cấu hình…
        </div>
      </div>
    );
  }

  if (forbidden) {
    return (
      <div>
        <PageHeader title="Cấu hình AI" breadcrumb="Quản trị › Cấu hình AI" />
        <Card>
          <CardBody className="py-16 text-center">
            <div className="inline-grid place-items-center h-14 w-14 rounded-full bg-destructive/10 text-destructive mb-4">
              <ShieldOff className="h-6 w-6" strokeWidth={1.75} />
            </div>
            <div className="font-serif italic text-2xl text-foreground mb-2">
              Bạn không có quyền
            </div>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              Chỉ quản trị viên (ADMIN) mới xem và sửa được cấu hình AI.
            </p>
          </CardBody>
        </Card>
      </div>
    );
  }

  if (isError || !form) {
    return (
      <div>
        <PageHeader title="Cấu hình AI" breadcrumb="Quản trị › Cấu hình AI" />
        <div className="text-sm text-muted-foreground py-10 text-center">
          Không tải được cấu hình. Vui lòng thử lại sau.
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Cấu hình AI"
        breadcrumb="Quản trị › Cấu hình AI"
        actions={
          <Button size="md" onClick={handleSave} disabled={saving}>
            {saving ? (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-1.5" />
            )}
            {saving ? "Đang lưu…" : "Lưu cấu hình"}
          </Button>
        }
      />

      {saveMsg && (
        <div
          className={`mb-4 text-sm rounded-md border px-3 py-2 ${
            saveMsg.ok
              ? "border-success/30 bg-success/10 text-success"
              : "border-destructive/30 bg-destructive/10 text-destructive"
          }`}
        >
          {saveMsg.text}
        </div>
      )}

      <Card className="mb-4">
        <CardHeader>
          <CardTitle>Kết nối nhà cung cấp AI</CardTitle>
        </CardHeader>
        <CardBody>
          <Field label="Endpoint (base URL)">
            <input
              className={inputClass}
              value={form.baseUrl}
              onChange={(e) => setForm({ ...form, baseUrl: e.target.value })}
              placeholder="https://api.openai.com/v1"
            />
          </Field>
          <Field
            label="Khoá API"
            hint={
              data?.hasKey
                ? `Đang dùng khoá: ${data.keyHint}. Chỉ gõ khoá mới nếu muốn thay đổi.`
                : "Chưa cấu hình khoá API."
            }
          >
            <input
              type="password"
              className={inputClass}
              value={form.apiKeyInput}
              onChange={(e) => setForm({ ...form, apiKeyInput: e.target.value })}
              placeholder={data?.hasKey ? (data.keyHint ?? "") : "Nhập khoá API…"}
              autoComplete="new-password"
            />
          </Field>
          <div className="flex items-center gap-3 mt-2 sm:pl-[232px]">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleTestConfig}
              disabled={testing}
            >
              {testing ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <Plug className="h-4 w-4 mr-1.5" />
              )}
              Kiểm tra cấu hình
            </Button>
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground sm:pl-[232px]">
            Chỉ kiểm tính hợp lệ của cấu hình đã lưu trên máy chủ (endpoint đúng URL, đã có khoá API
            hay chưa) — KHÔNG gọi thử tới nhà cung cấp AI, để tránh tốn tiền mỗi lần bấm.
          </p>
          {testMsg && (
            <p
              className={`mt-2 text-xs sm:pl-[232px] ${
                testMsg.ok ? "text-success" : "text-destructive"
              }`}
            >
              {testMsg.text}
            </p>
          )}
        </CardBody>
      </Card>

      <Card className="mb-4">
        <CardHeader>
          <CardTitle>Model sử dụng</CardTitle>
        </CardHeader>
        <CardBody>
          <Field label="Model kiểm tra thể thức">
            <input
              className={inputClass}
              value={form.modelSpellcheck}
              onChange={(e) => setForm({ ...form, modelSpellcheck: e.target.value })}
            />
          </Field>
          <Field label="Model tóm tắt văn bản">
            <input
              className={inputClass}
              value={form.modelSummary}
              onChange={(e) => setForm({ ...form, modelSummary: e.target.value })}
            />
          </Field>
          <Field label="Model đọc ảnh (văn bản dạng ảnh)">
            <input
              className={inputClass}
              value={form.modelVision}
              onChange={(e) => setForm({ ...form, modelVision: e.target.value })}
            />
          </Field>
        </CardBody>
      </Card>

      <Card className="mb-4">
        <CardHeader>
          <CardTitle>Bật/tắt tính năng</CardTitle>
        </CardHeader>
        <CardBody>
          <Field label="Kiểm tra thể thức (AI)">
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-input accent-primary-600"
                checked={form.enableSpellcheck}
                onChange={(e) => setForm({ ...form, enableSpellcheck: e.target.checked })}
              />
              <span className="text-muted-foreground">
                {form.enableSpellcheck ? "Đang bật" : "Đang tắt"}
              </span>
            </label>
          </Field>
          <Field label="Tóm tắt văn bản (AI)">
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-input accent-primary-600"
                checked={form.enableSummary}
                onChange={(e) => setForm({ ...form, enableSummary: e.target.checked })}
              />
              <span className="text-muted-foreground">
                {form.enableSummary ? "Đang bật" : "Đang tắt"}
              </span>
            </label>
          </Field>
        </CardBody>
      </Card>

      <Card className="mb-4">
        <CardHeader>
          <CardTitle>Luật kiểm tra thể thức bị tắt</CardTitle>
        </CardHeader>
        <CardBody>
          <Field
            label="Mã luật bị tắt"
            hint="Đây là các mã luật đã đo được BÁO NHẦM nhiều (Task 13, đo trên văn bản đã ban hành thật — xem docs/ai-review-false-positive-2026-08-16.md). Danh sách mã, phân tách bằng dấu phẩy, không dấu (vd PAGE_SIZE_NOT_A4,FONT_NOT_TIMES). Để trống nghĩa là KHÔNG tắt luật nào."
          >
            <input
              className={inputClass}
              value={form.disabledRules}
              onChange={(e) => setForm({ ...form, disabledRules: e.target.value })}
              placeholder="PAGE_SIZE_NOT_A4,FONT_SIZE_OUT_OF_RANGE,..."
            />
          </Field>
        </CardBody>
      </Card>

      <Card className="mb-4">
        <CardHeader>
          <CardTitle>Hạn mức chi tiêu</CardTitle>
        </CardHeader>
        <CardBody>
          <Field label="Ngân sách theo tháng (USD)" hint="0 = không giới hạn">
            <input
              type="number"
              min={0}
              step="0.01"
              className={inputClass}
              value={form.monthlyBudgetUsd}
              onChange={(e) => setForm({ ...form, monthlyBudgetUsd: e.target.value })}
            />
          </Field>
          <Field label="Giới hạn lượt/người/ngày" hint="0 = không giới hạn">
            <input
              type="number"
              min={0}
              step="1"
              className={inputClass}
              value={form.perUserDailyLimit}
              onChange={(e) => setForm({ ...form, perUserDailyLimit: e.target.value })}
            />
          </Field>
        </CardBody>
      </Card>
    </div>
  );
}
